<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.customerCourse.actions.bookMakeup.title')"
    :info="$t('components.customerCourse.actions.bookMakeup.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.customerCourse.actions.bookMakeup.confirmButton'), type: 'confirm', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
    :valid="validate"
  >
    <div class="pt-4 text-left">
      <div v-if="loadingData" class="flex items-center justify-center py-8">
        <o-loader class="h-8 w-8" />
      </div>

      <template v-else-if="loadError">
        <p class="text-sm text-gray-500 text-center py-4">
          {{ $t('components.customerCourse.actions.bookMakeup.loadError') }}
        </p>
      </template>

      <template v-else-if="!eligible">
        <p class="text-sm text-gray-500 text-center py-4">
          {{ $t('components.customerCourse.actions.bookMakeup.notEligible') }}
        </p>
      </template>

      <template v-else>
        <o-field-group
          :label="$t('components.customerCourse.actions.bookMakeup.makeupSession')"
          :info="$t('components.customerCourse.actions.bookMakeup.makeupSessionInfo')"
          class="block"
        >
          <div class="border rounded-lg dark:border-gray-800 overflow-hidden max-h-60 overflow-y-auto">
            <template v-if="reservations.length === 0">
              <p class="text-sm text-gray-500 text-center py-4">
                {{ $t('components.customerCourse.actions.bookMakeup.noAvailable') }}
              </p>
            </template>
            <label
              v-for="item in reservations"
              :key="item.reservation.idReservation"
              :class="[
                'flex items-center justify-between p-3 cursor-pointer border-b dark:border-gray-800 last:border-none',
                { 'bg-primary-50 dark:bg-primary-900': selectedReservation === item.reservation.idReservation }
              ]"
            >
              <div class="flex items-center">
                <input
                  v-model="selectedReservation"
                  :value="item.reservation.idReservation"
                  type="radio"
                  class="mr-3"
                >
                <div>
                  <p class="text-sm font-medium">
                    <template v-if="item.reservation.service?.name">{{ item.reservation.service.name }} - </template>{{ item.reservation.infoLabel }}<template v-if="item.reservation.employee"> ({{ item.reservation.employee.displayName }})</template>
                  </p>
                  <p class="text-xs text-gray-500">
                    {{ $date.convert(item.reservation.date, undefined, $date.presets.long) }}
                  </p>
                </div>
              </div>
              <o-chip size="sm" variant="success" flat>
                {{ $t('components.customerCourse.actions.bookMakeup.spacesAvailable', { count: item.remainingSpaces }) }}
              </o-chip>
            </label>
          </div>
        </o-field-group>
      </template>
    </div>
  </o-dialog>
</template>

<script>
import { OFieldGroup, OChip, OLoader } from '@/components/elements'

export default {
  components: {
    OFieldGroup,
    OChip,
    OLoader,
  },
  props: {
    portal: {
      type: String,
      default: undefined,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data () {
    return {
      customerCourse: null,
      idReservationCustomer: null,
      loadingData: false,
      loadError: false,
      eligible: false,
      reservations: [],
      selectedReservation: null,
    }
  },
  methods: {
    async open (customerCourse, idReservationCustomer) {
      this.idReservationCustomer = idReservationCustomer
      this.selectedReservation = null
      this.loadError = false
      this.eligible = false
      this.reservations = []

      this.$refs.dialog.open()

      // If customerCourse doesn't have model methods, fetch the full model
      if (typeof customerCourse.availableMakeups !== 'function') {
        this.loadingData = true
        try {
          const response = await this.$api.customerCourse.get(customerCourse.idCustomerCourse)
          this.customerCourse = response.data
        } catch (e) {
          console.error(e)
          this.$notification.error({ errors: e?.errors })
          this.loadError = true
          this.loadingData = false
          return
        }
      } else {
        this.customerCourse = customerCourse
      }

      await this.fetchAvailableMakeups()
    },
    async fetchAvailableMakeups () {
      this.loadingData = true
      this.loadError = false

      try {
        const data = await this.$api.customerCourse.availableMakeups(this.customerCourse, {
          idReservationCustomer: this.idReservationCustomer,
        })
        this.eligible = data.eligible
        this.reservations = data.reservations || []
      } catch (e) {
        console.error(e)
        this.$notification.error({ errors: e?.errors })
        this.loadError = true
      } finally {
        this.loadingData = false
      }
    },
    validate () {
      return !this.loadError && this.selectedReservation !== null
    },
    async confirm () {
      this.$emit('update:loading', true)

      try {
        const response = await this.$api.customerCourse.bookMakeup(this.customerCourse, {
          idReservationCustomer: this.idReservationCustomer,
          idReservation: this.selectedReservation,
        })
        const customerCourse = response.data

        if (customerCourse) {
          this.$emit('success', customerCourse)

          this.$bus.emit.updated('CustomerCourse', customerCourse)

          this.$notification.toast({
            title: this.$t('components.customerCourse.actions.bookMakeup.success'),
            text: this.$t('components.customerCourse.actions.bookMakeup.successInfo'),
            variant: 'success',
          })

          this.$emit('update:loading', false)
          this.$refs.dialog.close()

          return true
        }
      } catch (e) {
        console.error(e)
        this.$notification.error({ errors: e?.errors })
      }

      this.$emit('update:loading', false)
    },
  },
}
</script>
